/*----------------------------------------------------------------------------*\
  #BULK DOCUMENT UPLOAD

  Lets a single upload form submit several documents at once, each with its own
  editable File Name and Visibility. Kept entirely separate from document-upload.js
  (a different set of selectors, a different module here) so the qualifications
  and user-documentation upload forms that still use the single-file clone-block
  pattern are unaffected by this.

  A per-container state array (file/name/visibility per selected file) is the
  single source of truth. The master file input is the *only* input ever
  assigned a FileList -- always rebuilt from that same state, never handed to a
  fresh input the user never interacted with -- and the name/visibility hidden
  fields are rebuilt from the same array in the same pass, so the three can
  never drift out of position relative to one another.
\*----------------------------------------------------------------------------*/

// Imports
import { reinit as _reinit } from 'util/methods';

// Default module export
export default function(options) {
  // Private: Default settings object
  const _defaults = {
    selector: '.js-bulkDocumentUpload',
    $selectors() {
      return document.querySelectorAll(this.selector);
    },
    exists() {
      return this.$selectors().length > 0;
    },
    hoverClass: 'is-dragHover',
    uploadField: '.js-bulkDocumentUploadField',
    picker: '.js-bulkDocumentUploadPicker',
    rowsList: '.js-bulkDocumentUploadRows',
    rowTemplate: '.js-bulkDocumentUploadRowTemplate',
    hiddenFields: '.js-bulkDocumentUploadHiddenFields',
    rowFilename: '.js-bulkDocumentUploadRowFilename',
    rowNameInput: '.js-bulkDocumentUploadRowName',
    rowRemoveButton: '.js-bulkDocumentUploadRowRemove',
    rowVisibilityInput: 'input[type="radio"]',
    rowRecipientsSelect: '.js-bulkDocumentUploadRowRecipients',
    rowOperativesWrap: '.js-bulkDocumentUploadRowOperativesWrap',
    rowOperativesMount: '.js-bulkDocumentUploadRowOperativesMount',
    rowIndexToken: 'row_0',
  };

  // Private: Merge passed in object with defaults
  const _settings = {
    ..._defaults,
    ...options,
  };

  // Private: one state array per upload container
  const _state = new WeakMap();

  // Private: Hover state
  const _hoverState = (event, $target = event.currentTarget) => {
    $target.closest(_settings.selector).classList.add(_settings.hoverClass);
  };

  // Private: Blur state
  const _blurState = (event, $target = event.currentTarget) => {
    $target.closest(_settings.selector).classList.remove(_settings.hoverClass);
  };

  // Private: Only files matching the input's accept list are added as a row
  const _validFile = (file, $picker) => {
    if ($picker.accept.length === 0) return true;

    const filename = file.name;
    const validExtensions = $picker.accept.toLowerCase().split(',');
    const extension = filename.substr(filename.lastIndexOf('.')).toLowerCase();

    return validExtensions.indexOf(extension) >= 0;
  };

  // Private: Rebuild the hidden name/visibility fields from state, in order --
  // called on every add/remove/edit so they always match state exactly.
  const _syncHiddenFields = $docUpload => {
    const items = _state.get($docUpload) || [];
    const $hiddenFields = $docUpload.querySelector(_settings.hiddenFields);

    $hiddenFields.innerHTML = '';

    items.forEach(item => {
      const $name = document.createElement('input');
      $name.type = 'hidden';
      $name.name = 'site_document[document_names][]';
      $name.value = item.name;

      const $visibility = document.createElement('input');
      $visibility.type = 'hidden';
      $visibility.name = 'site_document[document_visibilities][]';
      $visibility.value = item.visibility;

      const $recipientsMode = document.createElement('input');
      $recipientsMode.type = 'hidden';
      $recipientsMode.name = 'site_document[document_recipients_modes][]';
      $recipientsMode.value = item.recipientsMode;

      const $recipientSelections = document.createElement('input');
      $recipientSelections.type = 'hidden';
      $recipientSelections.name =
        'site_document[document_recipient_selections][]';
      $recipientSelections.value = item.operatives
        .map(operative => operative.id)
        .join(',');

      $hiddenFields.append(
        $name,
        $visibility,
        $recipientsMode,
        $recipientSelections
      );
    });
  };

  // Private: React roots mounted into rows are about to be wiped out along
  // with the rest of the row markup in _renderRows below -- unmount them
  // first so React can clean up rather than leaving a "removed container"
  // warning (and a leaked instance) behind.
  const _unmountOperativePickers = $docUpload => {
    if (!window.OperativeRecipientsPicker) return;

    $docUpload
      .querySelectorAll(_settings.rowOperativesMount)
      .forEach($mount => {
        window.OperativeRecipientsPicker.unmount($mount);
      });
  };

  // Private: Rebuild the master picker's FileList from state -- the only input
  // this module ever assigns `.files` on is this same one, since it's the one
  // the user actually picked/dropped files into.
  const _syncPickerFiles = $docUpload => {
    const items = _state.get($docUpload) || [];
    const $picker = $docUpload.querySelector(_settings.picker);
    const transfer = new DataTransfer();

    items.forEach(item => transfer.items.add(item.file));
    $picker.files = transfer.files;
  };

  // Private: Rebuild the visible rows from state
  const _renderRows = $docUpload => {
    const items = _state.get($docUpload) || [];
    const $rowsList = $docUpload.querySelector(_settings.rowsList);
    const $template = $docUpload.querySelector(_settings.rowTemplate);

    _unmountOperativePickers($docUpload);
    $rowsList.innerHTML = '';

    items.forEach((item, index) => {
      const markup = $template.innerHTML.replace(
        new RegExp(_settings.rowIndexToken, 'g'),
        `row_${index}`
      );

      const $wrapper = document.createElement('div');
      $wrapper.innerHTML = markup.trim();
      const $row = $wrapper.firstElementChild;

      $row.querySelector(_settings.rowFilename).textContent = item.file.name;

      const $nameInput = $row.querySelector(_settings.rowNameInput);
      $nameInput.value = item.name;
      $nameInput.addEventListener('input', () => {
        item.name = $nameInput.value;
        _syncHiddenFields($docUpload);
      });

      // "Review & Signature Required" needs a PDF to actually render in the
      // in-browser viewer operatives sign it from -- disabled per row (not
      // globally) since each row can carry a different file, and falls back to
      // "Internal" if a non-PDF row somehow already carried that selection in.
      const isPdf = /\.pdf$/i.test(item.file.name);
      if (!isPdf && item.visibility === 'review') item.visibility = 'internal';

      // Mirrors SiteDocument#normalize_recipients_mode_for_visibility server-side:
      // Internal has no operative-facing recipients concept at all (forced to
      // "organization"), and leaving Internal always resets to the ordinary
      // "all_operatives" default rather than silently restoring a previous pick.
      const normalizeRecipientsForVisibility = () => {
        if (item.visibility === 'internal') {
          item.recipientsMode = 'organization';
        } else if (item.recipientsMode === 'organization') {
          item.recipientsMode = 'all_operatives';
        }
      };

      normalizeRecipientsForVisibility();

      const $recipientsSelect = $row.querySelector(
        _settings.rowRecipientsSelect
      );
      const $operativesWrap = $row.querySelector(_settings.rowOperativesWrap);
      const $operativesMount = $row.querySelector(_settings.rowOperativesMount);

      // Looked up once (rather than re-querying inside syncRecipientsUI) since
      // that function removes/re-inserts this same node -- a fresh query would
      // return null once it's been removed.
      const $organizationOption = $recipientsSelect.querySelector(
        'option[value="organization"]'
      );

      // Reflects item.recipientsMode/operatives into the select, the "Specify
      // Operatives" wrap's visibility, and (re)mounts the React picker there --
      // called on setup and whenever recipientsMode or the row's own visibility
      // changes.
      const syncRecipientsUI = () => {
        const isInternal = item.visibility === 'internal';

        // "Organization" only ever applies to Internal -- removed from the
        // <select> entirely the rest of the time (rather than just hidden/
        // disabled), matching normalizeRecipientsForVisibility above. The
        // hidden attribute on <option> isn't reliably respected by every
        // browser's native dropdown rendering, so actually removing the node
        // is what's dependable.
        if ($organizationOption) {
          if (isInternal && !$recipientsSelect.contains($organizationOption)) {
            $recipientsSelect.prepend($organizationOption);
          } else if (
            !isInternal &&
            $recipientsSelect.contains($organizationOption)
          ) {
            $organizationOption.remove();
          }
        }

        $recipientsSelect.value = item.recipientsMode;
        $recipientsSelect.disabled = isInternal;

        const isSpecific = item.recipientsMode === 'specific_operatives';
        $operativesWrap.classList.toggle('u-hide', !isSpecific);

        if (isSpecific && window.OperativeRecipientsPicker) {
          window.OperativeRecipientsPicker.mount($operativesMount, {
            siteId: $operativesMount.dataset.siteId,
            value: item.operatives,
            onChange: newValue => {
              item.operatives = newValue;
              _syncHiddenFields($docUpload);
              // AsyncSelect's value is controlled by the prop above -- without
              // re-rendering here, a newly picked (or removed) chip would
              // update item.operatives/the hidden fields but never actually
              // show up in the picker itself.
              syncRecipientsUI();
            },
          });
        }
      };

      $row.querySelectorAll(_settings.rowVisibilityInput).forEach($radio => {
        $radio.checked = $radio.value === item.visibility;

        if ($radio.value === 'review' && !isPdf) {
          $radio.disabled = true;
          const $tooltip = $radio
            .closest('.o-radio')
            .querySelector('.js-tooltip');
          if ($tooltip)
            $tooltip.dataset.tooltip = 'Only applicable for PDF documents.';
        }

        $radio.addEventListener('change', () => {
          if (!$radio.checked) return;

          item.visibility = $radio.value;
          normalizeRecipientsForVisibility();
          syncRecipientsUI();
          _syncHiddenFields($docUpload);
        });
      });

      $recipientsSelect.addEventListener('change', () => {
        item.recipientsMode = $recipientsSelect.value;
        if (item.recipientsMode !== 'specific_operatives') item.operatives = [];

        syncRecipientsUI();
        _syncHiddenFields($docUpload);
      });

      syncRecipientsUI();

      $row
        .querySelector(_settings.rowRemoveButton)
        .addEventListener('click', () => {
          _state.get($docUpload).splice(index, 1);
          _renderRows($docUpload);
          _syncPickerFiles($docUpload);
          _syncHiddenFields($docUpload);
        });

      $rowsList.appendChild($row);
    });

    // Rows clone their own .js-tooltip markup out of a <template>, invisible to
    // tooltips' own init() (which only ever runs once, over whatever .js-tooltip
    // elements existed at page load) -- reinit it so freshly-rendered rows'
    // tooltips actually respond to hover/focus.
    _reinit('application', 'tooltips');
  };

  // Private: Add one state entry per valid selected/dropped file, then
  // rebuild rows, the picker's FileList, and the hidden fields together.
  const _reportFiles = (
    event,
    $target = event.currentTarget,
    files = event.currentTarget.files
  ) => {
    if (!files || files.length === 0) return;

    const $docUpload = $target.closest(_settings.selector);
    const items = _state.get($docUpload) || [];

    [...files]
      .filter(file => _validFile(file, $target))
      .forEach(file =>
        items.push({
          file,
          name: file.name,
          visibility: 'internal',
          recipientsMode: 'organization',
          operatives: [],
        })
      );

    _state.set($docUpload, items);

    _renderRows($docUpload);
    _syncPickerFiles($docUpload);
    _syncHiddenFields($docUpload);
  };

  // Private: Clear out any rows left over from a cancelled upload
  const _resetRows = event => {
    const $docUpload = event.currentTarget.querySelector(_settings.selector);
    if (!$docUpload) return;

    _state.set($docUpload, []);
    _renderRows($docUpload);
    _syncHiddenFields($docUpload);
  };

  // Private: One confirm per row set to Review & Signature Required --
  // .every short-circuits on the first decline, so declining the first
  // never goes on to ask about the rest of the batch.
  const _confirmSubmit = event => {
    const $docUpload = event.currentTarget.querySelector(_settings.selector);
    if (!$docUpload) return;

    const items = _state.get($docUpload) || [];
    const confirmed = items
      .filter(item => item.visibility === 'review')
      .every(item =>
        window.confirm(
          `Uploading ${item.name} with a Review & Signature Required visibility will inform your selected recipients via email to sign the document. Are you sure you want to proceed?`
        )
      );

    if (!confirmed) event.preventDefault();
  };

  // Public: Destroy module instance
  const destroy = () => {
    if (!_settings.exists()) return;

    _settings.$selectors().forEach($docUpload => {
      const $uploadField = $docUpload.querySelector(_settings.uploadField);
      const $picker = $docUpload.querySelector(_settings.picker);
      const $form = $docUpload.closest('form');

      $uploadField.removeEventListener('dragenter', _hoverState);
      $uploadField.removeEventListener('dragleave', _blurState);
      $uploadField.removeEventListener('drop', _blurState);
      $picker.removeEventListener('change', _reportFiles);
      $form && $form.removeEventListener('reset', _resetRows);
      $form && $form.removeEventListener('submit', _confirmSubmit);
    });
  };

  // Public: Destroy module instance and run initialise again
  const reinit = () => {
    destroy();
    init();
  };

  // Public: Initialise module
  const init = () => {
    if (!_settings.exists()) return;

    _settings.$selectors().forEach($docUpload => {
      const $uploadField = $docUpload.querySelector(_settings.uploadField);
      const $picker = $docUpload.querySelector(_settings.picker);
      const $form = $docUpload.closest('form');

      _state.set($docUpload, []);

      $uploadField.addEventListener('dragenter', _hoverState);
      $uploadField.addEventListener('dragleave', _blurState);
      $uploadField.addEventListener('drop', _blurState);
      $picker.addEventListener('change', _reportFiles);
      $form && $form.addEventListener('reset', _resetRows);
      $form && $form.addEventListener('submit', _confirmSubmit);
    });
  };

  // Return public methods
  return {
    destroy,
    reinit,
    init,
  };
}
